import React, {useState,useEffect} from 'react';
import { createPortal } from 'react-dom';
import moment from 'moment';
import fileDownload from 'js-file-download';
import {BsFillPersonFill as Person} from 'react-icons/bs';
import {BsCheckCircle as Completed} from 'react-icons/bs';
import {BsClockFill as Clock} from 'react-icons/bs';
import Download from '@mui/icons-material/ArrowDownward';
import LinearProgress from '@mui/material/LinearProgress';
import CircularProgress from '@mui/material/CircularProgress';
import TicketExportJobService from '@Helpers/URLHelpers/TicketExportJobService';
import UserInfoService from '@Helpers/URLHelpers/UserInfoService';
import FileService from '@Helpers/URLHelpers/FileService';
import ErrorHandler from '@Root/ErrorHandler';
import './TicketExportJobRow.scss';

export default function TicketExportJobRow(props){

    const [cursor, setCursor] = useState('auto');
    const [ticketExportJob, setTicketExportJob] = useState();
    const [isDownloading, setIsDownloading] = useState(false);
    const [status,setStatus] = useState(props.item.status);
    const [conclusion, setConclusion] = useState(props.item.conclusion);
    const [user, setUser] = useState({});
    const ticketExportJobService = new TicketExportJobService();
    const userInfoService = new UserInfoService();
    const fileService = new FileService();
    const errorHandler = new ErrorHandler();
    const className = 'ticketExportJobRow';

    let interval;

    useEffect(()=>{
        renderData();
        interval = setInterval(() => {
            fetchStatus();
        }, 10000);
        return () => {
            clearInterval(interval);
        }
    },[status])

    const renderData = async ()=>{
        try {
            let user = await userInfoService.getSimpleUserById(props.item.executionUserId);
            user = JSON.parse(user.body);
            setUser(user);
        } catch (err){
            errorHandler.handleError(err);
        }
        setTicketExportJob(props.item);
        await fetchStatus();
    }

    const fetchStatus = async()=>{
        try {
            if(status !== 'completed') {
                let data = await ticketExportJobService.getTicketExportJobById(props.item.id);
                if(!data?.body) {
                    return;
                }
                data = JSON.parse(data.body);
                setStatus(data.status);
                setConclusion(data.conclusion);
                setTicketExportJob(data);
            }
        } catch (err) {
            errorHandler.handleError(err);
        }
    }

    const Capitalize = (str) => {
        return str?.charAt(0)?.toUpperCase() + str?.slice(1);
    }

    const isFailed = conclusion === 'failed';
    const isProcessing = status === 'processing' && !isFailed;
    const isCompleted = status === 'completed' && !isFailed;

    const getFailureReason = () => {
        try {
            const raw = ticketExportJob?.details ?? props.item?.details;
            if (!raw) {
                return '';
            }
            const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
            return parsed?.error || '';
        } catch (err) {
            return '';
        }
    };

    const fullPageOverlay = isDownloading && typeof document !== 'undefined'
        ? createPortal(
            <div className={className + '__overlay'} role='status' aria-live='polite'>
                <div className={className + '__overlay__content'}>
                    <CircularProgress size={34} />
                    <span>Preparing download...</span>
                </div>
            </div>,
            document.body
        )
        : null;

    const handleDownload = async () => {
        if (isDownloading) {
            return;
        }
        const storageId = ticketExportJob?.excelFileStorageId;
        if (!storageId || storageId === 0) {
            errorHandler.handleError({ message: 'No file is available for this export. Please run the export again.' });
            return;
        }
        downloadFile(storageId);
    }

    const dataURLtoFile = (dataurl) => {
        var arr = dataurl?.split(','),
            mime = arr[0]?.match(/:(.*?);/)[1],
            bstr = atob(arr[1]),
            n = bstr.length,
            u8arr = new Uint8Array(n);
        while (n--) {
            u8arr[n] = bstr.charCodeAt(n);
        }
        return new File([u8arr], { type: mime });
    };

    const downloadFile = async(id) => {
        setCursor('wait');
        setIsDownloading(true);
        try {
            let fileObj = await fileService.getFile(id);
            if (!fileObj?.body) {
                throw new Error('The export file could not be retrieved. Please run the export again.');
            }
            const fileRecord = JSON.parse(fileObj.body);
            const fileClob = fileRecord?.fileClob ? JSON.parse(fileRecord.fileClob) : null;
            if (!fileClob?.file || !fileClob?.fileName) {
                throw new Error('The export file is missing or corrupted. Please run the export again.');
            }
            fileDownload(dataURLtoFile(fileClob.file), fileClob.fileName);
        } catch (err) {
            errorHandler.handleError({ message: err?.message || 'Download failed. Please try again.' });
        } finally {
            setCursor('auto');
            setIsDownloading(false);
        }
    }

    return (
        <div className={className + ' status-' + (isFailed ? 'failed' : (status || 'unknown'))} style={{cursor: cursor}}>
            <div className={className + '__top'}>
                <div className={className + '__top__left'}>
                    <div className={className + '__top__left__index'}>
                        #{props.index}
                    </div>
                    <div className={className + '__top__left__user'}>
                        <Person className={className + '__top__left__user__icon'} />
                        <div className={className + '__top__left__user__meta'}>
                            <div className={className + '__top__left__user__meta__label'}>
                            Executed By
                            </div>
                            <div className={className + '__top__left__user__meta__name'}>
                                {user?.userName || 'Unknown User'}
                            </div>
                        </div>
                    </div>
                </div>
                <div className={className + '__top__right'}>
                    {
                        isFailed ?
                            <div className={className + '__top__right__status failed'}>
                                <span>Failed</span>
                            </div>
                        : isProcessing ?
                            <div className={className + '__top__right__status processing'}>
                                <CircularProgress size={14} thickness={5} className={className + '__top__right__status__spinner'} />
                                <span>{Capitalize(status)}</span>
                            </div>
                        :
                            <div className={className + '__top__right__status ' + status}>
                                <Completed className={className + '__top__right__status__icon'} />
                                <span>{Capitalize(status)}</span>
                            </div>
                    }
                </div>
            </div>
            <div className={className + '__bottom'}>
                <div className={className + '__bottom__left'}>
                    <Clock className={className + '__bottom__left__icon'}/>
                    <span>{moment(new Date(props.item.createTimeStamp),'DD-MM-YYYY').fromNow()}</span>
                </div>
                <div className={className + '__bottom__right'}>
                    {
                        isFailed ?
                            <span className={className + '__bottom__right__failed'}>
                                {getFailureReason() || 'Export failed. Please run it again.'}
                            </span>
                        : isCompleted &&
                            <button
                                type='button'
                                className={className + '__bottom__right__download'}
                                onClick={handleDownload}
                                disabled={isDownloading || ticketExportJob?.excelFileStorageId == 0}
                                title={ticketExportJob?.excelFileStorageId == 0 ? 'File is not ready yet' : 'Download file'}>
                                <Download/>
                                <span>Download</span>
                            </button>
                    }
                </div>
            </div>

            {isProcessing && <LinearProgress className={className + '__progress'} />}

            {fullPageOverlay}
        </div>
    )
}